import sortBy from 'lodash-es/sortBy';

export function getCompetitionName(competition: {
  name: string;
  parent?: { name: string } | null;
}) {
  if (competition.parent) {
    return `${competition.parent.name} ${competition.name}`;
  }

  return competition.name;
}

export function getCompetitionLogoId(competition: {
  logo?: { id: string } | null;
  parent?: { logo?: { id: string } | null } | null;
}) {
  if (competition.logo) {
    return competition.logo.id;
  }

  if (competition.parent?.logo) {
    return competition.parent.logo.id;
  }

  return;
}

export function getCompetitionUrl(competition: {
  slug: string;
}): `/competition/${string}` {
  return `/competition/${competition.slug}`;
}

export function getSeasonUrl(
  competition: { slug: string },
  season: { slug: string },
): `/competition/${string}/${string}` {
  const competitionSlug = competition.slug;
  const seasonSlug = season.slug.replace(competitionSlug + '-', '');

  return `/competition/${competitionSlug}/${seasonSlug}`;
}

export function getSeasonStandingsUrl(
  competition: { slug: string },
  season: { slug: string },
): `/competition/${string}/${string}/standings` {
  return `${getSeasonUrl(competition, season)}/standings`;
}

export function getMatchdayUrl(
  competition: { slug: string },
  season: { slug: string },
  matchday: { slug: string },
): `/competition/${string}/${string}/${string}` {
  const competitionSlug = competition.slug;
  const seasonSlug = season.slug.replace(competitionSlug + '-', '');
  const matchdaySlug = matchday.slug.replace(season.slug + '-', '');

  return `/competition/${competitionSlug}/${seasonSlug}/${matchdaySlug}`;
}

export function getBattleUrl(battle: { id: string }): `/battle/${string}` {
  return `/battle/${battle.id}`;
}

export function getProfileUrl(profile: {
  username: string;
}): `/profile/${string}` {
  return `/profile/${profile.username}`;
}

export function setInternationalFirst<
  T extends { name: string } | { competition: { name: string } | null },
>(collection: T[]) {
  return sortBy(collection, (item) => {
    const name = 'competition' in item ? item.competition?.name : item.name;

    if (name === 'World Series') {
      return 0;
    } else if (name === 'Internacional') {
      return 1;
    } else {
      return 2;
    }
  });
}

export function formatMatchdayStartDatetimeShort(dateString: string) {
  const unknownTime = dateString.endsWith('T00:00:01.000Z');

  const date = new Date(dateString);

  const dateFormatted = date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    ...(unknownTime && { timeZone: 'UTC' }),
  });

  const [day, _month, time] = dateFormatted.split(' ');

  return { day, month: _month.replace(',', ''), time };
}

export function formatMatchdayStartDatetime(dateString: string) {
  const unknownTime = dateString.endsWith('T00:00:01.000Z');

  const date = new Date(dateString);

  const dateFormatted = date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(unknownTime
      ? { timeZone: 'UTC' }
      : { hour: 'numeric', minute: 'numeric' }),
  });

  return dateFormatted;
}

export function filterNulls<T>(array: (T | null)[]): T[] {
  return array.filter(
    (item): item is NonNullable<typeof item> => item !== null,
  );
}

export function getRoleName(role: string) {
  switch (role) {
    case 'as_freestyler':
      return 'Freestyler';
    case 'as_judge':
      return 'Juez';
    case 'as_host':
      return 'Host';
    case 'as_dj':
      return 'DJ';
    default:
      return role;
  }
}

export function getParticipantTypeName(role: string) {
  switch (role) {
    case 'matchdays':
      return 'jornadas';
    case 'battles':
      return 'batallas';
    case 'battles_victories':
      return 'victorias';
    case 'battles_defeats':
      return 'derrotas';
    case 'medals':
      return 'medallas';
    case 'medals_first':
      return '🥇';
    case 'medals_second':
      return '🥈';
    case 'medals_third':
      return '🥉';
    default:
      return role;
  }
}
